import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import ContactForm from "@/components/ContactForm";
import { MapPin, Phone, Mail, Building2, LineChart, ShieldCheck } from "lucide-react";

const IMG_HERO = "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=1600&q=80";
const IMG_INTRO = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1400&q=80";
const IMG_TREND = "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1400&q=80";
const IMG_P1 = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80";
const IMG_P2 = "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1000&q=80";
const IMG_P3 = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1000&q=80";

export default function Home() {
  const { t } = useI18n();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* HERO */}
      <section className="zx-hero">
        <div className="zx-hero__inner">
          <div className="zx-hero__text">
            <span className="zx-eyebrow">{t("hero.eyebrow")}</span>
            <h1 className="zx-hero__title">{t("hero.title")}</h1>
            <p className="zx-hero__subtitle">{t("hero.subtitle")}</p>
            <div className="zx-hero__ctas">
              <a href="#contact" onClick={scrollTo("contact")} className="zx-btn zx-btn--primary zx-btn--lg">
                {t("hero.ctaPrimary")}
              </a>
              <a href="#how" onClick={scrollTo("how")} className="zx-btn zx-btn--outline zx-btn--lg">
                {t("hero.ctaSecondary")}
              </a>
            </div>
            <dl className="zx-hero__stats">
              <div>
                <dt>{t("hero.stat1Label")}</dt>
                <dd>{t("hero.stat1Value")}</dd>
              </div>
              <div>
                <dt>{t("hero.stat2Label")}</dt>
                <dd>{t("hero.stat2Value")}</dd>
              </div>
              <div>
                <dt>{t("hero.stat3Label")}</dt>
                <dd>{t("hero.stat3Value")}</dd>
              </div>
            </dl>
          </div>
          <div className="zx-hero__media">
            <img src={IMG_HERO} alt={t("hero.imageAlt")} loading="eager" />
            <div className="zx-hero__mediaBadge">
              <ShieldCheck size={18} />
              <span>EU Reg. 2020/1503</span>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="zx-section zx-intro">
        <div className="zx-section__inner zx-intro__grid">
          <div className="zx-intro__media">
            <img src={IMG_INTRO} alt={t("intro.imageAlt")} loading="lazy" />
          </div>
          <div className="zx-intro__text">
            <span className="zx-kicker">{t("intro.kicker")}</span>
            <h2 className="zx-h2">{t("intro.heading")}</h2>
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
            <p>{t("intro.p3")}</p>
            <p>{t("intro.p4")}</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="zx-section zx-how" id="how">
        <div className="zx-section__inner">
          <div className="zx-section__head">
            <span className="zx-kicker">{t("how.kicker")}</span>
            <h2 className="zx-h2">{t("how.heading")}</h2>
            <p className="zx-lead">{t("how.lead")}</p>
          </div>
          <ol className="zx-steps">
            <li className="zx-step">
              <h3>{t("how.step1Title")}</h3>
              <p>{t("how.step1Body")}</p>
            </li>
            <li className="zx-step">
              <h3>{t("how.step2Title")}</h3>
              <p>{t("how.step2Body")}</p>
            </li>
            <li className="zx-step">
              <h3>{t("how.step3Title")}</h3>
              <p>{t("how.step3Body")}</p>
            </li>
            <li className="zx-step">
              <h3>{t("how.step4Title")}</h3>
              <p>{t("how.step4Body")}</p>
            </li>
          </ol>
        </div>
      </section>

      {/* TREND / TRANSPARENCY */}
      <section className="zx-section zx-trend">
        <div className="zx-section__inner zx-trend__grid">
          <div className="zx-trend__text">
            <span className="zx-kicker">{t("trend.kicker")}</span>
            <h2 className="zx-h2">{t("trend.heading")}</h2>
            <p>{t("trend.p1")}</p>
            <p><strong>{t("trend.p2")}</strong></p>
            <p>{t("trend.p3")}</p>
            <p>{t("trend.p4")}</p>
            <a href="#contact" onClick={scrollTo("contact")} className="zx-btn zx-btn--outline">
              {t("hero.ctaPrimary")}
            </a>
          </div>
          <div className="zx-trend__media">
            <img src={IMG_TREND} alt={t("trend.imageAlt")} loading="lazy" />
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="zx-section zx-projects" id="projects">
        <div className="zx-section__inner">
          <div className="zx-section__head">
            <span className="zx-kicker">{t("projects.kicker")}</span>
            <h2 className="zx-h2">{t("projects.heading")}</h2>
            <p className="zx-lead">{t("projects.lead")}</p>
          </div>
          <div className="zx-cards">
            {[{img:IMG_P1,k:"p1"},{img:IMG_P2,k:"p2"},{img:IMG_P3,k:"p3"}].map((c) => (
              <article key={c.k} className="zx-card">
                <div className="zx-card__media">
                  <img src={c.img} alt={t(`projects.${c.k}Title`)} loading="lazy" />
                </div>
                <div className="zx-card__body">
                  <span className="zx-card__loc"><MapPin size={14} /> {t(`projects.${c.k}Location`)}</span>
                  <h3>{t(`projects.${c.k}Title`)}</h3>
                  <p>{t(`projects.${c.k}Body`)}</p>
                  <div className="zx-card__foot">
                    <span className="zx-card__meta"><LineChart size={14} /> {t(`projects.${c.k}Meta`)}</span>
                    <a href="#contact" onClick={scrollTo("contact")} className="zx-btn zx-btn--sm zx-btn--outline">
                      {t("projects.cardCta")}
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className="zx-disclaimer">{t("projects.disclaimer")}</p>
        </div>
      </section>

      {/* ABOUT */}
      <section className="zx-section zx-about" id="about">
        <div className="zx-section__inner">
          <div className="zx-section__head zx-section__head--center">
            <span className="zx-kicker">{t("about.kicker")}</span>
            <h2 className="zx-h2">{t("about.heading")}</h2>
          </div>
          <div className="zx-about__grid">
            <div className="zx-about__text">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
            </div>
            <ul className="zx-values">
              <li>
                <Building2 size={20} />
                <h4>{t("about.value1")}</h4>
                <p>{t("about.value1Body")}</p>
              </li>
              <li>
                <MapPin size={20} />
                <h4>{t("about.value2")}</h4>
                <p>{t("about.value2Body")}</p>
              </li>
              <li>
                <ShieldCheck size={20} />
                <h4>{t("about.value3")}</h4>
                <p>{t("about.value3Body")}</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="zx-section zx-contact" id="contact">
        <div className="zx-section__inner zx-contact__grid">
          <div className="zx-contact__intro">
            <span className="zx-kicker">{t("contact.kicker")}</span>
            <h2 className="zx-h2">{t("contact.heading")}</h2>
            <p className="zx-lead">{t("contact.lead")}</p>
            <ul className="zx-contact__list">
              <li>
                <Mail size={16} />
                <a href={`mailto:${t("contact.emailAddress")}`}>{t("contact.emailAddress")}</a>
              </li>
              <li>
                <Phone size={16} />
                <a href={`tel:${t("contact.phone").replace(/\s/g, "")}`}>{t("contact.phone")}</a>
              </li>
              <li>
                <MapPin size={16} />
                <span>{t("contact.address")}</span>
              </li>
            </ul>
          </div>
          <div className="zx-contact__form">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
